import React from 'react';
import { Outlet } from 'react-router-dom';
import { AppShell as Shell, AnimatedPage, type NavSection } from '@theonefamily/ui';
import { HubBar } from '@theonefamily/hub-bar';
import { useAuth } from './contexts/AuthContext.js';
import { useNetworkStatus } from './hooks/useNetworkStatus.js';
import {
  LayoutDashboard,
  CalendarClock,
  CalendarDays,
  Map,
  Briefcase,
  Users,
  Building2,
  Wrench,
  Package,
  Clock,
  BarChart3,
  Receipt,
  Sparkles,
  Settings,
  ClipboardList,
} from 'lucide-react';

const BASE_NAV: NavSection[] = [
  {
    items: [
      { id: 'dashboard',   label: 'Dashboard',   icon: LayoutDashboard, href: '/dashboard' },
      { id: 'jobs',        label: 'Jobs',        icon: Briefcase,       href: '/jobs' },
      { id: 'calendar',    label: 'Calendar',    icon: CalendarDays,    href: '/calendar' },
      { id: 'map',         label: 'Map',         icon: Map,             href: '/map' },
      { id: 'clients',     label: 'Clients',     icon: Building2,       href: '/clients' },
      { id: 'technicians', label: 'Technicians', icon: Wrench,          href: '/technicians' },
    ],
  },
];

const DISPATCHER_SECTION: NavSection = {
  label: 'Dispatch',
  items: [
    { id: 'dispatch', label: 'Dispatch Board', icon: CalendarClock, href: '/dispatch' },
  ],
};

const OPERATIONS_SECTION: NavSection = {
  label: 'Operations',
  items: [
    { id: 'reports',  label: 'Reports',  icon: BarChart3, href: '/reports' },
    { id: 'invoices', label: 'Invoices', icon: Receipt,   href: '/invoices' },
  ],
};

const ADMIN_SECTION: NavSection = {
  label: 'Admin',
  items: [
    { id: 'audit-log', label: 'Audit Log', icon: ClipboardList, href: '/audit-log' },
    { id: 'settings',  label: 'Settings',  icon: Settings,      href: '/settings' },
  ],
};

export default function AppShell() {
  const { user, isDispatcher, signOut } = useAuth();
  const isOnline = useNetworkStatus();

  const navigation = isDispatcher
    ? [DISPATCHER_SECTION, ...BASE_NAV, OPERATIONS_SECTION, ADMIN_SECTION]
    : [...BASE_NAV, OPERATIONS_SECTION, ADMIN_SECTION];

  return (
    <>
      {!isOnline && (
        <div className="sticky top-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium">
          Offline — queuing actions
        </div>
      )}
      <HubBar
        currentProduct="onsite"
        apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonestack.com'}
        hubUrl={import.meta.env.VITE_HUB_URL ?? 'https://app.theonestack.com'}
        supportConfig={{
          apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
          apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
          platformId: 'onsite',
          platformName: 'The One Onsite',
        }}
      />
      <Shell
        product={{ name: 'The One Onsite', accent: '#f97316' }}
        navigation={navigation}
        user={{
          name: user?.name ?? user?.email ?? 'User',
          email: user?.email ?? '',
          role: user?.role,
        }}
        onSignOut={signOut}
      >
        <AnimatedPage>
          <Outlet />
        </AnimatedPage>
      </Shell>

      {/* THEO floating button */}
      <button
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg shadow-primary/30 transition-transform hover:scale-105 active:scale-95"
        aria-label="THEO AI Assistant"
        onClick={() => {
          const event = new CustomEvent('theo:open', { detail: { context: 'onsite' } });
          window.dispatchEvent(event);
        }}
      >
        <Sparkles className="h-6 w-6 text-primary-foreground" />
      </button>
    </>
  );
}
